import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Target, Phone, CheckCircle2, Percent, DollarSign, TrendingDown, Sparkles,
  AlertTriangle, Filter, ChevronDown, ChevronRight, Ban, Flame,
} from "lucide-react";
import { cn } from "../lib/utils";
import DateFilter from "../components/DateFilter";
import { SyncDataButton } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (v) => Number(v || 0).toLocaleString("en-US");

const BADGE = {
  "Top Performer": "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Average: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Poor: "bg-red-500/15 text-red-400 border-red-500/30",
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function BuyerOptimizerPage() {
  const [dateFilter, setDateFilter] = useState(null);
  const [offerFilter, setOfferFilter] = useState("all");
  const [buyerFilter, setBuyerFilter] = useState("");
  const [threshold, setThreshold] = useState(20);
  const [appliedThreshold, setAppliedThreshold] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const fetchData = useCallback(async (silent = false) => {
    if (!dateFilter) return;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateFilter.startDate, end_date: dateFilter.endDate,
        threshold: String(appliedThreshold), ai: "true",
      });
      if (offerFilter && offerFilter !== "all") params.set("offer", offerFilter);
      if (buyerFilter) params.set("buyer", buyerFilter);
      const res = await axios.get(`${API}/api/buyer-optimizer/data?${params}`, { headers: authHeaders() });
      setData(res.data);
      setLastSynced(new Date().toISOString());
      if (res.data.ringba_unavailable) toast.error(res.data.ringba_warning || "Ringba unavailable");
    } catch (err) {
      console.error("Buyer optimizer fetch error:", err);
      toast.error("Failed to load buyer performance data");
    } finally {
      setLoading(false);
    }
  }, [dateFilter, offerFilter, buyerFilter, appliedThreshold]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/buyer-optimizer/sync`, {}, { headers: authHeaders() });
      await fetchData(true);
      toast.success("Buyer data synced from Ringba");
    } catch {
      toast.error("Sync failed. Please try again.");
    } finally {
      setSyncing(false);
    }
  };

  const totals = data?.totals || {};
  const offers = data?.offers || [];
  const rowsByOffer = useMemo(() => {
    const m = {};
    (data?.rows || []).forEach((r) => { (m[r.offer] = m[r.offer] || []).push(r); });
    return m;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="buyer-optimizer-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>Buyer Performance Optimizer</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Offer × buyer conversion performance, live from Ringba</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5">
            <Filter className="h-3.5 w-3.5 text-slate-500" />
            <select value={offerFilter} onChange={(e) => setOfferFilter(e.target.value)} className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer max-w-[220px]" data-testid="bo-offer-filter">
              <option value="all" className="bg-slate-900">All Offers</option>
              {(data?.filters?.offers || []).map((o) => <option key={o} value={o} className="bg-slate-900">{o}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5">
            <Target className="h-3.5 w-3.5 text-slate-500" />
            <select value={buyerFilter} onChange={(e) => setBuyerFilter(e.target.value)} className="bg-transparent text-sm text-slate-300 outline-none cursor-pointer max-w-[200px]" data-testid="bo-buyer-filter">
              <option value="" className="bg-slate-900">All Buyers</option>
              {(data?.filters?.buyers || []).map((b) => <option key={b} value={b} className="bg-slate-900">{b}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/50 rounded-lg px-3 py-1.5" title="Poor-performer threshold">
            <Percent className="h-3.5 w-3.5 text-slate-500" />
            <input
              type="number" min={0} max={100} value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              onBlur={() => setAppliedThreshold(Math.max(0, Math.min(100, Number(threshold) || 0)))}
              onKeyDown={(e) => e.key === "Enter" && setAppliedThreshold(Math.max(0, Math.min(100, Number(threshold) || 0)))}
              className="bg-transparent text-sm text-slate-300 outline-none w-12 text-right"
              data-testid="bo-threshold-input"
            />
            <span className="text-xs text-slate-500">% threshold</span>
          </div>
          <SyncDataButton onSync={handleSync} syncing={syncing} lastSynced={lastSynced} ringbaUnavailable={!!data?.ringba_unavailable} testId="sync-data-btn" />
        </div>
      </div>

      <DateFilter onFilterChange={setDateFilter} defaultPreset="last_30_days" />

      {loading ? (
        <div className="flex items-center justify-center py-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi title="Total Calls" value={fmtNum(totals.calls)} sub={`${totals.offers || 0} offers · ${totals.buyers || 0} buyers`} icon={Phone} color="bg-blue-500/10 text-blue-400" testId="bo-kpi-calls" />
            <Kpi title="Converted" value={fmtNum(totals.converted)} sub={`${totals.conversion_rate ?? 0}% overall`} icon={CheckCircle2} color="bg-emerald-500/10 text-emerald-400" testId="bo-kpi-converted" />
            <Kpi title="Revenue" value={fmtMoney(totals.revenue)} sub={`Profit ${fmtMoney(totals.profit)}`} icon={DollarSign} color="bg-amber-500/10 text-amber-400" testId="bo-kpi-revenue" />
            <Kpi title="Poor Buyers" value={fmtNum(totals.poor_buyers)} sub={`below ${appliedThreshold}%`} icon={TrendingDown} color="bg-red-500/10 text-red-400" testId="bo-kpi-poor" />
            <Kpi title="Est. Revenue Lost" value={fmtMoney(totals.est_revenue_lost)} sub="from poor performers" icon={Flame} color="bg-orange-500/10 text-orange-400" testId="bo-kpi-lost" />
          </div>

          <PerformanceTable offers={offers} rowsByOffer={rowsByOffer} collapsed={collapsed} setCollapsed={setCollapsed} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Recommendations aiSummary={data?.ai_summary} aiSource={data?.ai_source} recommendations={data?.recommendations || []} />
            <LosingMoney losing={data?.losing_money} />
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ title, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-4 flex items-start gap-3" data-testid={testId}>
      <div className={cn("p-2.5 rounded-lg shrink-0", color)}><Icon className="h-4 w-4" /></div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{title}</p>
        <p className="text-xl font-bold text-slate-100 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
        {sub && <p className="text-[11px] text-slate-500 truncate">{sub}</p>}
      </div>
    </div>
  );
}

function PerformanceTable({ offers, rowsByOffer, collapsed, setCollapsed }) {
  if (!offers.length) {
    return <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-12 text-center text-slate-500 text-sm" data-testid="bo-empty">No Ringba call data for this range / filter.</div>;
  }
  return (
    <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="bo-performance-table">
      <div className="px-4 py-3 border-b border-slate-800/50 flex items-center gap-2">
        <Target className="h-4 w-4 text-blue-400" />
        <h2 className="text-sm font-semibold text-slate-200">Buyer vs Offer Performance</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2.5 font-medium">Offer / Buyer</th>
              <th className="text-right px-4 py-2.5 font-medium">Calls</th>
              <th className="text-right px-4 py-2.5 font-medium">Converted</th>
              <th className="text-right px-4 py-2.5 font-medium">Conv. Rate</th>
              <th className="text-right px-4 py-2.5 font-medium">Revenue</th>
              <th className="text-right px-4 py-2.5 font-medium">Payout</th>
              <th className="text-left px-4 py-2.5 font-medium">Performance</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o, oi) => {
              const open = !collapsed[o.offer];
              const rows = rowsByOffer[o.offer] || [];
              return [
                <tr key={o.offer} className="bg-slate-800/30 border-b border-slate-800/50 cursor-pointer hover:bg-slate-800/50" onClick={() => setCollapsed((c) => ({ ...c, [o.offer]: !c[o.offer] }))} data-testid={`bo-offer-row-${oi}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-100">
                    <span className="group inline-flex items-center gap-1.5">
                      {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                      {o.offer}
                      <CopyButton text={o.offer} label="offer name" testId={`copy-offer-${oi}`} />
                      <span className="text-[11px] font-normal text-slate-500 ml-1">{o.buyers} buyer{o.buyers !== 1 ? "s" : ""}{o.unrouted_calls ? ` · ${fmtNum(o.unrouted_calls)} unrouted` : ""}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-200">{fmtNum(o.calls)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-200">{fmtNum(o.converted)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-100">{o.conversion_rate}%</td>
                  <td className="px-4 py-2.5 text-right text-amber-300">{fmtMoney(o.revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-400">{fmtMoney(o.payout)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{o.poor_buyers ? <span className="text-red-400">{o.poor_buyers} poor</span> : "—"}</td>
                </tr>,
                ...(open ? rows.map((r, ri) => (
                  <tr key={`${o.offer}-${r.target}`} className="group border-b border-slate-800/30 hover:bg-white/[0.02]" data-testid={`bo-buyer-row-${oi}-${ri}`}>
                    <td className="px-4 py-2 pl-12">
                      <span className="inline-flex items-center gap-1 text-slate-200">
                        {r.target}
                        <CopyButton text={r.target} label="buyer target" testId={`copy-target-${oi}-${ri}`} />
                      </span>
                      <span className="block text-[11px] text-slate-500">{r.buyer}{r.low_sample ? " · low sample" : ""}</span>
                    </td>
                    <td className="px-4 py-2 text-right text-slate-300">{fmtNum(r.calls)}</td>
                    <td className="px-4 py-2 text-right text-slate-300">{fmtNum(r.converted)}</td>
                    <td className={cn("px-4 py-2 text-right font-semibold", r.badge === "Poor" ? "text-red-400" : r.badge === "Top Performer" ? "text-emerald-400" : "text-slate-200")}>{r.conversion_rate}%</td>
                    <td className="px-4 py-2 text-right text-amber-200/80">{fmtMoney(r.revenue)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{fmtMoney(r.payout)}</td>
                    <td className="px-4 py-2">
                      <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border", BADGE[r.badge])} data-testid={`bo-badge-${oi}-${ri}`}>{r.badge}</span>
                    </td>
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const renderBold = (line, key) => {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return <p key={key} className="text-sm text-slate-300 leading-relaxed">{parts.map((p, i) => p.startsWith("**") ? <strong key={i} className="text-slate-100">{p.slice(2, -2)}</strong> : p)}</p>;
};

function Recommendations({ aiSummary, aiSource, recommendations }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="bo-recommendations">
      <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-400" /><h2 className="text-sm font-semibold text-slate-200">AI Optimization Recommendations</h2></div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{aiSource === "gpt-4o-mini" ? "AI + rules" : "rules"}</span>
      </div>
      <div className="p-4 space-y-4 max-h-[560px] overflow-y-auto">
        {aiSummary && (
          <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-3 space-y-2" data-testid="bo-ai-summary">
            {aiSummary.split("\n").filter((l) => l.trim()).map((l, i) => renderBold(l.trim(), i))}
          </div>
        )}
        {recommendations.length === 0 && <p className="text-sm text-slate-500">No recommendations for this selection.</p>}
        {recommendations.map((rec, i) => (
          <div key={rec.offer} data-testid={`bo-reco-${i}`}>
            <p className="text-xs font-semibold text-slate-200 mb-1">{rec.offer} <span className="text-slate-500 font-normal">· {rec.conversion_rate}% overall</span></p>
            <ul className="space-y-1">
              {rec.lines.map((l, j) => (
                <li key={j} className={cn("text-xs leading-relaxed pl-3 border-l-2", l.includes("highest") ? "border-emerald-500/50 text-slate-300" : l.includes("pausing") || l.includes("reduce") ? "border-red-500/50 text-slate-300" : "border-slate-700 text-slate-400")}>{l}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function LosingMoney({ losing }) {
  const [showAll, setShowAll] = useState(false);
  const poor = losing?.poor_buyers || [];
  const visiblePoor = showAll ? poor : poor.slice(0, 25);
  const weak = losing?.weak_offers || [];
  return (
    <div className="bg-slate-900/60 border border-red-500/20 rounded-xl overflow-hidden" data-testid="bo-losing-money">
      <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between">
        <div className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /><h2 className="text-sm font-semibold text-slate-200">Where You Are Losing Money</h2></div>
        <span className="text-sm font-bold text-red-400" data-testid="bo-lost-total">{fmtMoney(losing?.est_revenue_lost)} est. lost</span>
      </div>
      <div className="p-4 space-y-5 max-h-[560px] overflow-y-auto">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Buyers below {losing?.threshold ?? 20}% conversion ({poor.length})</p>
          {poor.length === 0 ? <p className="text-xs text-slate-500">None — every buyer is above the threshold.</p> : (
            <div className="space-y-1.5">
              {visiblePoor.map((r, i) => (
                <div key={`${r.offer}-${r.target}`} className="flex items-center justify-between gap-3 text-xs bg-red-500/5 border border-red-500/10 rounded-lg px-3 py-2" data-testid={`bo-poor-${i}`}>
                  <div className="min-w-0">
                    <p className="text-slate-200 truncate"><Ban className="inline h-3 w-3 text-red-400 mr-1" />{r.target} <span className="text-slate-500">· {r.offer}</span></p>
                    <p className="text-slate-500">{fmtNum(r.calls)} calls · {r.converted} converted · <span className="text-red-400 font-semibold">{r.conversion_rate}%</span></p>
                  </div>
                  <span className="text-orange-300 font-semibold whitespace-nowrap">-{fmtMoney(r.est_revenue_lost)}</span>
                </div>
              ))}
              {poor.length > 25 && (
                <button onClick={() => setShowAll((v) => !v)} className="text-xs text-blue-400 hover:text-blue-300 px-1 py-1" data-testid="bo-poor-show-more">
                  {showAll ? "Show less" : `Show ${poor.length - 25} more`}
                </button>
              )}
            </div>
          )}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">High volume, low conversion offers ({weak.length})</p>
          {weak.length === 0 ? <p className="text-xs text-slate-500">None — high-volume offers convert above the threshold.</p> : (
            <div className="space-y-1.5">
              {weak.map((o, i) => (
                <div key={o.offer} className="flex items-center justify-between gap-3 text-xs bg-amber-500/5 border border-amber-500/10 rounded-lg px-3 py-2" data-testid={`bo-weak-${i}`}>
                  <p className="text-slate-200 truncate">{o.offer}</p>
                  <p className="text-slate-400 whitespace-nowrap">{fmtNum(o.calls)} calls · <span className="text-amber-400 font-semibold">{o.conversion_rate}%</span></p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
